import { DateTime } from 'luxon';
import { dateFormat, dateTimeFormat, timeFormat } from '@/utils/dateFormat';
import { useIntl } from 'react-intl';

export const formatDate = (date: string) => {
  return DateTime.fromISO(date).toFormat(dateFormat);
};

export const toApiDateTimeRange = (
  dateFrom?: string,
  dateTo?: string,
): { from?: string; to?: string } => {
  const from = dateFrom
    ? DateTime.fromFormat(dateFrom, 'yyyy-MM-dd').startOf('day').toISO()
    : null;
  const to = dateTo
    ? DateTime.fromFormat(dateTo, 'yyyy-MM-dd').endOf('day').toISO()
    : null;

  return {
    from: from ?? undefined,
    to: to ?? undefined,
  };
};

export const formatDateTime = (date: string) => {
  return DateTime.fromISO(date).toFormat(dateTimeFormat);
};

/**
 * Formats a date/time with only the relevant parts relative to the current
 * time:
 * - Same day: only the time (e.g. "14:32")
 * - Same year, different day: month, day and time (e.g. "4 maj 14:32")
 * - Different year: full date and time (e.g. "2025-05-04, 14:32")
 */
export const formatRelativeDateTime = (date: string) => {
  const target = DateTime.fromISO(date);
  if (!target.isValid) return date;

  const now = DateTime.now();

  if (target.hasSame(now, 'day')) {
    return target.toFormat(timeFormat);
  }

  if (target.hasSame(now, 'year')) {
    return target.setLocale('sv').toFormat(`d MMM ${timeFormat}`);
  }

  return target.toFormat(dateTimeFormat);
};

export const formatTimeLeft = (
  date: DateTime,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  const now = DateTime.now();
  const diff = date.diff(now, ['days', 'hours', 'minutes']).toObject();
  let timeLeftStr = '';

  if (diff.days && diff.days >= 1) {
    timeLeftStr = formatMessage(
      {
        defaultMessage: '{num, plural, one {1 dag kvar} other {# dagar kvar}}',
      },
      { num: Math.floor(diff.days) },
    );
  } else if (diff.hours && diff.hours >= 1) {
    timeLeftStr = formatMessage(
      {
        defaultMessage:
          '{num, plural, one {1 timme kvar} other {# timmar kvar}}',
      },
      { num: Math.floor(diff.hours) },
    );
  } else if (diff.minutes && diff.minutes >= 1) {
    timeLeftStr = formatMessage(
      {
        defaultMessage:
          '{num, plural, one {1 minut kvar} other {# minuter kvar}}',
      },
      { num: Math.floor(diff.minutes) },
    );
  } else {
    timeLeftStr = formatMessage({ defaultMessage: 'Utgått' });
  }

  return timeLeftStr;
};

/**
 * Formats a date/time using full Swedish words instead of digits-only,
 * making it pronounceable for text-to-speech / screen readers.
 * Example: "4 maj 2026 kl 12:05".
 */
export const formatDateTimeReadable = (date: string) => {
  return DateTime.fromISO(date)
    .setLocale('sv')
    .toFormat("d MMMM yyyy 'kl' HH:mm");
};

export const formatCardNumber = (cardNumber: string) => {
  return cardNumber.replace(/\B(?=(\d{4})+(?!\d))/g, ' ');
};

/**
 * Converts a 10-character Swedish personnummer (`YYMMDDXXXX`, `YYMMDD-XXXX`,
 * or `YYMMDD+XXXX`, where `XXXX` may be a TF suffix such as `TF12`) to its
 * 12-character form (`YYYYMMDDXXXX`). The century is derived from the current
 * year and the separator: a `+` indicates the person is 100+ years old.
 *
 * Returns the separator-free form when input is already 12 characters, and
 * returns the original value unchanged when the input doesn't look like a
 * 10- or 12-character personnummer.
 */
export const convertNinTo12Digit = (value: string): string => {
  const trimmed = value.trim();
  const compact = trimmed.replace(/[-+]/g, '');

  if (compact.length === 12) return compact;
  if (compact.length !== 10) return trimmed;

  const yy = parseInt(compact.slice(0, 2), 10);
  if (Number.isNaN(yy)) return trimmed;

  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;

  let fullYear = currentCentury + yy;
  if (fullYear > currentYear) {
    fullYear -= 100;
  }
  if (trimmed.includes('+')) {
    fullYear -= 100;
  }

  return `${fullYear}${compact.slice(2)}`;
};

export const formatPersonnummer = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '');

  if (!digitsOnly) return value;

  let formattedNumber = digitsOnly;

  if (digitsOnly.length === 10) {
    const firstDigit = parseInt(digitsOnly[0]);
    const centuryPrefix = firstDigit <= 3 ? '20' : '19';
    formattedNumber = centuryPrefix + digitsOnly;
  }

  if (formattedNumber.length === 10 || formattedNumber.length === 12) {
    const beforeDash = formattedNumber.slice(0, -4);
    const afterDash = formattedNumber.slice(-4);
    return beforeDash + '-' + afterDash;
  }

  return formattedNumber;
};
